import { toFixed } from './calc';
import { isTypeOf } from './general';

/**
 * Format price. Example: 25.50 -> "25,50 €". Rounded to 2 decimal places.
 *
 * @returns {string} Formatted price or empty string if price is not a number or string.
 */
export function formatPrice(
  price: string | number,
  /**
   * Whether to add the currency sign.
   */
  addCurrency = true,
  /**
   * Whether to keep the decimals if they are 0.
   */
  keepDecimals = true
) {
  if (!['number', 'string'].includes(typeof price)) {
    price = 0;
  }

  const [euros, cents = '00'] = toFixed(price, 2).split('.');
  let formattedPrice = keepDecimals ? '0,00' : '0';

  if (!Number.isInteger(Number.parseInt(euros!))) {
    return formattedPrice;
  }

  const decimal = ',';

  if (euros && (cents !== '00' || keepDecimals)) {
    formattedPrice = [euros, cents].join(decimal);
  } else if (euros) {
    formattedPrice = euros;
  }

  return addCurrency ? `${formattedPrice}\u00A0€` : formattedPrice;
}

/**
 * Format percentage. Example: 25.545 -> "25,55 %". Rounded to 2 decimal places.
 *
 * @returns Formatted percent or empty string if percent is not a number or string.
 */
export function formatPercentage(
  /**
   * The percentage to format.
   */
  percentage: string | number | undefined,
  /**
   * Whether to add the percentage sign.
   */
  addPercentage = true,
  /**
   * Whether to keep the decimals.
   */
  keepDecimals = false,
  /**
   * The number of decimals to keep.
   */
  numberOfDecimals = 2
) {
  let formattedPercentage = keepDecimals ? '0,00' : '0';

  if ([Infinity, -Infinity, NaN].includes(Number(percentage))) {
    return addPercentage ? '∞\u00A0%' : '∞';
  }

  if (!isTypeOf(percentage, ['number', 'string'])) {
    return formattedPercentage;
  }

  const [integers, decimals] = toFixed(percentage, numberOfDecimals).split('.');

  if (!Number.isInteger(Number.parseInt(String(integers)))) {
    return formattedPercentage;
  }

  const decimal = ',';

  if (integers && (+(decimals ?? 0) !== 0 || keepDecimals)) {
    formattedPercentage = [integers, keepDecimals ? decimals : +(decimals ?? 0)].join(decimal);
  } else if (integers) {
    formattedPercentage = integers;
  }

  return addPercentage ? `${formattedPercentage}\u00A0%` : formattedPercentage;
}

/**
 * Format VAT. Example: 25.50 -> "25,5 %"". Rounded to 1 decimal place.
 *
 * @returns Formatted VAT or empty string if vat is not a number or string.
 */
export function formatVat(
  /**
   * The VAT to format.
   */
  vat: string | number,
  /**
   * Whether to add the percentage sign.
   */
  addPercentage = true
) {
  if (!['number', 'string'].includes(typeof vat)) {
    return '';
  }

  if (vat === '') {
    return '';
  }

  const [integers, decimals] = toFixed(vat, 1).split('.');

  if (!Number.isInteger(Number.parseInt(integers!))) {
    return '';
  }

  const decimal = ',';

  let formattedVat = '0';
  if (integers && decimals !== '0') {
    formattedVat = [integers, decimals].join(decimal);
  } else if (integers) {
    formattedVat = integers;
  }

  return addPercentage ? `${formattedVat}\u00A0%` : formattedVat;
}

/**
 * You can use this function to handle errors that might be thrown by a function and return a fallback value.
 *
 * Example: Since Big.js is used for calculations and it's being run in strict mode, it can throw an error
 * for example when there's loss of precision (or if input is not valid number).
 * You can use this to handle such cases with a fallback value.
 *
 * @returns {T} Result of the function or fallback value.
 */
export function handleFallbackValue<T>(
  /**
   * The function that might throw an error.
   */
  func: () => T,
  /**
   * The fallback value.
   */
  value: T
) {
  try {
    return func();
  } catch {
    return value;
  }
}

/**
 * Processes a number field value.
 *
 * @param {(string|number)} value - The value to be processed.
 * @param {boolean} [converToFixed=false] - If true, the value will be rounded to 2 decimal places.
 * @returns {string} The processed value.
 */
export function processPriceField(
  /**
   * The value to be processed.
   */
  value: string | number,
  /**
   * Whether to convert the value to a fixed number.
   */
  convertToFixed = false
) {
  const val = String(value || '0')
    .replace(/[^0-9,.]/g, '')
    .replaceAll(',', '.');

  return convertToFixed ? toFixed(val, 2) : val;
}

/**
 * Processes a percentage field value.
 *
 * @returns {string} The processed value.
 */
export function processPercentageField(
  /**
   * The value to be processed.
   */
  value: string | number,
  /**
   * Whether to convert the value to a fixed number.
   */
  convertToFixed: boolean | number = false,
  /**
   * Whether to limit the value between 0 and 100.
   */
  limitPercentage = false,
  /**
   * Whether to allow a trailing decimal.
   */
  allowTrailingDecimal = false
) {
  let val = String(value || '0');

  const isNegative = val.startsWith('-');

  val = val
    .replace(/[^0-9,.]/g, '')
    .replaceAll(',', '.')
    .replace(/^(\d*\.?)|(\d*)\.?/g, '$1$2');

  const addDecimal = allowTrailingDecimal && val.endsWith('.');

  val = String(+val);

  if (isNegative) {
    val = `-${val}`;
  }

  if (limitPercentage) {
    if (+val > 100) {
      val = '100';
    }

    if (+val < 0) {
      val = '0';
    }
  }

  let decimals = typeof convertToFixed === 'boolean' ? 2 : +convertToFixed;
  decimals = decimals >= 0 ? decimals : 2;

  return convertToFixed !== false ? toFixed(val, decimals) : String(addDecimal ? val + '.' : val);
}

/**
 * Formats a number input value.
 *
 * @returns {string} The formatted value.
 */
export function formatNumberInput(
  /**
   * The value to be formatted.
   */
  value: string | number,
  /**
   * Whether to round the value to 2 decimal places.
   */
  round = false,
  /**
   * Whether to return the fallback value if the input is empty.
   */
  fallback = false
) {
  const val = String(value).trim();
  if (val === '') {
    return fallback ? '0,00' : '';
  }

  if (val === ',' || val === '.') {
    return '0,';
  }

  return processPriceField(value, round).replace('.', ',');
}

/**
 * Formats a percentage input value. Recommended settings for onChange event: formatPercentageInput(e.target.value, false, false, true, true). Recommended settings for onBlur event: formatPercentageInput(e.target.value, true, true, true, false).
 *
 * @returns {string} The formatted value.
 */
export function formatPercentageInput(
  /**
   * The value to be formatted.
   */
  value: string | number,
  /**
   * Whether to return the fallback value if the input is empty.
   */
  fallback = false,
  /**
   * Whether to convert the value to a fixed number.
   */
  convertToFixed: boolean | number = false,
  /**
   * Whether to limit the value between 0 and 100.
   */
  limitPercentage = false,
  /**
   * Whether to allow a trailing decimal.
   */
  allowTrailingDecimal = true
) {
  const val = String(value).trim();
  if (val === '') {
    return fallback ? '0' : '';
  }

  if ((val === ',' || val === '.') && allowTrailingDecimal) {
    return '0,';
  }

  return processPercentageField(val, convertToFixed, limitPercentage, allowTrailingDecimal).replace(
    '.',
    ','
  );
}
